/**
 * 权限矩阵的唯一实现（见方案 2.2）。前端用它隐藏按钮，后端用它做真正的门禁。
 * 关键原则：前端隐藏只是体验，后端校验才是安全边界。
 */
import { SYSTEM_ADMIN_ROLE, type CreatorType, type SharePermission } from './constants.js';

/** 操作者：家长（带权限）或学生本人。 */
export type Actor =
  | { kind: 'parent'; parentId: string; permission: SharePermission }
  | { kind: 'student'; studentId: string };

/** 被操作对象的内容归属。 */
export interface ContentOwnership {
  createdByType: CreatorType;
  createdById: string;
}

// ---------- 学生级操作 ----------

/** 查看学生信息与统计：owner/editor/viewer 都可。 */
export function canViewStudent(actor: Actor): boolean {
  return actor.kind === 'parent' || actor.kind === 'student';
}

/** 编辑学生基本信息：仅 owner/editor。学生本人不可修改自己的档案（年级生日由家长维护）。 */
export function canEditStudentProfile(actor: Actor): boolean {
  return actor.kind === 'parent' && (actor.permission === 'owner' || actor.permission === 'editor');
}

/** 删除学生（软删）：仅 owner（见方案 1.2 ⑧）。 */
export function canDeleteStudent(actor: Actor): boolean {
  return actor.kind === 'parent' && actor.permission === 'owner';
}

/** 共享 / 改权限 / 取消共享：仅 owner。共享家长不能再转共享。 */
export function canManageShares(actor: Actor): boolean {
  return actor.kind === 'parent' && actor.permission === 'owner';
}

/** 重置学生密码：owner/editor。 */
export function canResetStudentPassword(actor: Actor): boolean {
  return actor.kind === 'parent' && (actor.permission === 'owner' || actor.permission === 'editor');
}

// ---------- 内容级操作（目标 / 任务 / 打卡任务） ----------

/**
 * 创建内容。
 * - 家长：owner/editor 可创建（家长侧）
 * - 学生：可创建自己的目标与任务
 */
export function canCreateContent(actor: Actor): boolean {
  if (actor.kind === 'student') return true;
  return actor.permission === 'owner' || actor.permission === 'editor';
}

/**
 * 编辑 / 删除某条内容。
 * 规则（见方案 1.2 ⑨）：
 * - 家长 owner/editor：可改可删【学生创建的内容】（监护权）
 * - 学生：**只能**改删【自己创建的】内容；家长创建的内容对学生的只读
 */
export function canMutateContent(actor: Actor, content: ContentOwnership): boolean {
  if (actor.kind === 'student') {
    return content.createdByType === 'student' && content.createdById === actor.studentId;
  }
  return actor.permission === 'owner' || actor.permission === 'editor';
}

/** 界面标注用：这条内容是"家长创建"还是"自己创建"。 */
export function ownershipLabel(content: ContentOwnership, actor: Actor): string {
  if (content.createdByType === 'parent') return '家长创建';
  if (actor.kind === 'student' && content.createdById === actor.studentId) return '我创建的';
  return '学生创建';
}

// ---------- 打卡级操作 ----------

/**
 * 打卡 / 计时：**只有学生本人**。
 *
 * ⚠️ 2026-10-16 收紧（用户拍板）。原来这里是"学生本人，或 owner/editor 家长代录"。
 *    改动的直接原因是"家长预览孩子端界面"这个功能被整体删除：那条路是家长唯一能
 *    点「完成」的地方，而它**不是沙盒**（实测家长点一次就给孩子 +300 分）。
 *    用户否掉了"预览只读 + 开关"的方案，原话是：
 *
 *    > "我觉得应该**彻底禁止**吧……家长就完全不能去改动、操作孩子的界面。
 *    >  即使有个开关，那他最终还是点那个开关，还是可以改的呀。"
 *
 *    所以这条判定改成了"只有孩子自己"。它守的是**孩子数据的可信性**：
 *    打卡记录、完成量、积分都必须是孩子自己产生的，否则孩子一旦发现
 *    家长能替他改，就不会再信任这个系统。
 *
 * ⛔ **不要**为了"方便"把它放宽回 owner/editor。家长要替孩子做事，
 *    应该走家长自己的界面与能力（见下面几个函数），而不是借用孩子的界面。
 * ⛔ 也**不要**在这里加"开关/代操作模式"——用户明确否决过。
 *
 * 覆盖面（路由层与服务层各判一次，见 `checkins.routes.ts`）：
 * 开始计时 / 暂停 / 继续 / 心跳 / 完成 / 补卡。
 * ⚠️ **读**路径不在这里：`GET /student/today`、`GET /checkins` 用的是
 *    `canViewStudent`（学生 + 三种档位的家长都能读）—— 用户的要求是
 *    "可以看，但不能操作"，拿写门禁当读门禁会把家长挡在看数据之外。
 */
export function canCheckin(actor: Actor): boolean {
  return actor.kind === 'student';
}

/**
 * 「放弃」**普通打卡任务**（非作业项）：学生本人 + 家长 owner/editor。
 *
 * ⚠️ 与 `canCheckin` 分开是必须的，别把两者合并：
 *  - `canCheckin`（2026-10-16 起）**只有学生**，管的是"打卡/完成/计时"这类
 *    **产生孩子数据**的动作；
 *  - 本函数管的是"这次不做了"这个**既有行为的保持**：普通打卡的「放弃」
 *    过去一直是学生与 owner/editor 家长都能点的，用户没有要求动它，
 *    改它会让家长端已有的能力悄悄消失。
 *  - 而**作业项**的「放弃」是第三种：只有家长能点（`canSkipHomeworkCheckin`）。
 *
 * 三者的分流在 `checkins.routes.ts` 的 `/checkins/:id/skip` 与服务层的
 * `skipCheckin()` 里各判一次（双保险）。
 */
export function canSkipCheckin(actor: Actor): boolean {
  if (actor.kind === 'student') return true;
  return actor.permission === 'owner' || actor.permission === 'editor';
}

/** 补卡（宽限期内）：与学生打卡同一条判定 —— 补卡同样是在**产生孩子的数据**。 */
export function canMakeup(actor: Actor): boolean {
  return canCheckin(actor);
}

/**
 * 放弃**作业项**（`node.kind = 'homework'` 的打卡）：**仅家长 owner/editor**。
 *
 * ⛔ 与 `canCheckin` 的差别就是本函数存在的全部理由。
 * 用户明确的产品决策是「跨天的未完成作业**一直保留**，直到完成、**或家长点击放弃**」——
 * 放弃意味着"这份作业不做了"，是一条会**关闭整科/整单完成判定**的终止决定，
 * 所以它属于家长的判断，不属于孩子。
 *
 * 为什么不做成"按 actor 判定"的通用开关：普通打卡任务（目标的叶子节点）的
 * 「放弃」是**已验收过**的既有行为（孩子可以自己说"今天这次不做了"），
 * 改动它会破坏已验证的功能。因此这里必须带上"这条打卡是不是作业项"这个前提 ——
 * 判定入口是 `isHomeworkCheckin()`（apps/api 的 checkins 服务按 `node.kind` 查库）。
 *
 * 判定形状与 `canApproveCheckin` / `canUndoCheckin` 完全一致：先看 `kind === 'parent'`，
 * 再看权限档位。⛔ 绝不能用 `canCreateContent`（对学生恒 true）或
 * `canMutateContent`（对学生自己创建的内容返回 true）代替 —— 那两条对孩子都返回 true。
 */
export function canSkipHomeworkCheckin(actor: Actor): boolean {
  return actor.kind === 'parent' && (actor.permission === 'owner' || actor.permission === 'editor');
}

/** 撤销打卡：**仅家长 owner/editor**（纠错权留给家长，避免孩子随意抹掉记录）。 */
export function canUndoCheckin(actor: Actor): boolean {
  return actor.kind === 'parent' && (actor.permission === 'owner' || actor.permission === 'editor');
}

/**
 * 「**撤销放弃**」（`skipped → pending`）：学生本人 **或** 家长 owner/editor。
 *
 * ⚠️ 存在的唯一理由（2026-10-17 用户实测的原话）：
 *
 * > "孩子打卡这里，孩子说想点完成时，**不小心点了放弃**，这个有什么方案？"
 *
 * 在此之前 `skipped` 只对家长开放（`canUndoCheckin` 走 `POST /checkins/:id/undo`），
 * 而**孩子端界面上根本没有撤销入口** —— 一次误触就把这一项锁死到今天结束。
 * 于是这里放开"孩子本人可以撤销自己刚点的放弃"，但**范围由服务层收窄**：
 *
 * | 谁 | 能恢复哪些 | 收窄在哪 |
 * | --- | --- | --- |
 * | 学生本人 | **只有自己今天**（`checkin.due_date` = 家庭时区的今天）的 `skipped` | `checkins.rules.ts` 的 `decideRestore()` |
 * | 学生本人 | ⛔ **不能**恢复作业项（`node.kind='homework'`） | 同上（作业的放弃本来就只有家长能点，撤销也该是家长） |
 * | 家长 owner/editor | 任何一天、任何类型 | 与 `canUndoCheckin` 同口径（纠错权） |
 * | 家长 viewer | ⛔ 一律不行 | 只读就是只读 |
 *
 * ⛔ 为什么**不**直接把 `canUndoCheckin` 放宽给学生：`undo` 还管
 *    `done → pending`（把孩子已完成的记录抹回未完成）。那条必须留在家长手里
 *    ——否则孩子可以自己把"完成"退回去，积分与连续天数就成了他手里的旋钮。
 *    所以恢复放弃是**另一个端点、另一个判定**（`POST /checkins/:id/restore`），
 *    而且只接受 `skipped` 这一个起点。
 *
 * 与 `canSkipCheckin` 的关系：这里是"把它退回来"，那边是"把它放弃掉"，
 * 名单刻意对齐（学生 + owner/editor），差别只在服务层对学生的日期/类型收窄。
 * 想改权限时**两个函数一起想**，别只改一个 —— 否则会出现"能放弃却不能撤销"，
 * 那正是用户这次报的 bug 的形状。
 */
export function canRestoreSkippedCheckin(actor: Actor): boolean {
  return canSkipCheckin(actor);
}

/**
 * 作业验收（通过 / 打回）：**仅家长 owner/editor**。
 *
 * ⛔ 第 0 条：**学生绝不可以验收**，包括他自己创建的作业。
 * 为什么不能图省事复用 `canMutateContent`：它对学生会返回
 * "自己创建的内容可以改" → `true`，于是孩子布置作业、自己点完成、
 * 再自己点"通过"，验收这一环就彻底形同虚设（而且不报错，只是分值悄悄到手）。
 * 所以这里与 `canUndoCheckin` 同形状：先看 `kind === 'parent'`，再看权限档位。
 *
 * 与 `canUndoCheckin` 的关系：撤销是"家长纠错"，验收是"家长确认"，
 * 两者都只属于家长；验收**不**额外要求 `habit.need_approval = true` ——
 * 状态 `submitted` 才是唯一事实。若家长中途把 `need_approval` 关掉，
 * 已提交的作业仍应能被通过，否则它会被永远卡在"待验收"。
 */
export function canApproveCheckin(actor: Actor): boolean {
  return actor.kind === 'parent' && (actor.permission === 'owner' || actor.permission === 'editor');
}

// ---------- 积分与兑换 ----------

/** 查看积分与流水：已确认"孩子完全可见"，故所有可见者都能看。 */
export function canViewPoints(actor: Actor): boolean {
  return canViewStudent(actor);
}

/** 手动奖励 / 扣分：仅家长 owner/editor。学生不能给自己加分。 */
export function canAdjustPoints(actor: Actor): boolean {
  return actor.kind === 'parent' && (actor.permission === 'owner' || actor.permission === 'editor');
}

/**
 * 发起兑换：**只有学生本人**。
 *
 * 与 `canCheckin` 同源（它直接转发），因为兑换同样是在**写孩子的数据**：
 * 它会扣孩子的积分、生成一条兑换申请。2026-10-16 随打卡一起收紧 ——
 * 家长替孩子下单，孩子看到的"我自己攒的分换来的东西"就不成立了。
 *
 * 家长该做的是**审批**（`canDecideRedemption`：通过 / 打回 / 发货），
 * 而不是替孩子发起。现实里"孩子想要、家长代点一下"的需求，应当由孩子自己点。
 */
export function canRequestRedemption(actor: Actor): boolean {
  return canCheckin(actor);
}

/** 审批 / 履约 / 驳回兑换：仅家长 owner/editor。 */
export function canDecideRedemption(actor: Actor): boolean {
  return actor.kind === 'parent' && (actor.permission === 'owner' || actor.permission === 'editor');
}

/** 维护奖品目录：仅家长 owner/editor。 */
export function canManageRewards(actor: Actor): boolean {
  return actor.kind === 'parent' && (actor.permission === 'owner' || actor.permission === 'editor');
}

// ---------- 点评 ----------

/** 写点评：家长 owner/editor；学生可对自己的打卡写自我小结。 */
export function canComment(actor: Actor): boolean {
  if (actor.kind === 'student') return true;
  return actor.permission === 'owner' || actor.permission === 'editor';
}

// ---------- 语文听写 · 批量任务（迁移 0020，2026-10-28） ----------

/**
 * **布置 / 暂停 / 结束 / 删除**听写批量任务：仅家长 owner/editor。
 *
 * 用户原话："孩子不能结束整个任务，家长可以结束、暂停或删除整个任务。"
 * 学生在这里恒为 `false` —— 这正是"孩子只能一批一批往下听，不能把任务关掉"
 * 这条产品规则在权限层的唯一落点。
 *
 * ⚠️ 与 `canCreateContent` 的区别：那个对学生恒 `true`（"学生可以创建自己的
 *    目标与任务"）。听写任务是**家长布置、孩子执行**的东西，所以这里显式要求
 *    家长身份 —— 与 `requireDictationFamilyWriter`（routes 里）同一个判断，
 *    放在 shared 是为了前后端共用同一份，界面上按钮的显隐也不必另写一套。
 */
export function canManageDictationAssignment(actor: Actor): boolean {
  return actor.kind === 'parent' && (actor.permission === 'owner' || actor.permission === 'editor');
}

/**
 * **开始一批听写 / 提交这一批**：学生本人 + 家长 owner/editor。
 *
 * 为什么学生在这里被放开（与 `canCheckin` 的"只有学生"方向相反）：
 *   · 「开始听写」与「提交」是在**产生孩子的数据**（一次 session、一批题），
 *     本来就该由孩子自己点 —— 用户的需求正是"这样就不用大人陪着"；
 *   · 但家长端也要能推进（过去家长端就能改会话题状态，那是**已验证的既有能力**），
 *     删掉它会让家长端出现"点了没反应"。
 *
 * 所以这里两人都放行；**真正的边界在别处**：孩子改不了任务本身
 * （`canManageDictationAssignment` 对学生恒 false），也看不到别人的任务
 * （`resolveStudentAccess` 的行级权限）。
 */
export function canPlayDictationSession(actor: Actor): boolean {
  if (actor.kind === 'student') return true;
  return actor.permission === 'owner' || actor.permission === 'editor';
}

/**
 * 断言工具：不满足即抛错，避免每个路由重复 if。
 * 这里只做布尔判断，抛什么错由调用方决定，保持 shared 包零依赖。
 */
export function assertPermission(allowed: boolean, message = '没有权限执行此操作'): void {
  if (!allowed) throw new Error(message);
}

// ---------- 系统管理员（见方案 2B） ----------
//
// 边界（方案 2B.1）：系统管理员管理**这个部署实例**（注册开关、账号、备份、
// 运行状态、审计），**不能查看任何家庭数据**（学生列表内容、打卡记录、积分明细、点评），
// 只能看计数类统计。
//
// 这些判断放在 shared 里，是为了让前后端用同一个事实来源：
// 前端用它决定路由与菜单，后端用它做真正的门禁（前端隐藏只是体验）。

/** 是否是系统管理员角色（只看角色，不看 TOTP 是否已绑定）。 */
export function isSystemAdmin(role: string | null | undefined): boolean {
  return role === SYSTEM_ADMIN_ROLE;
}

/**
 * 谁能访问**家庭数据**（学生档案、目标、打卡、积分、点评）：家长与学生本人。
 *
 * 系统管理员**刻意不在**范围内（方案 2B.1）：学生信息属于不满十四周岁
 * 未成年人的敏感个人信息，为运维便利而给管理员读这些数据的权限，
 * 等于把隐私风险常态化。它只能看计数类统计。
 *
 * 这条判断是所有"家庭数据接口"的第一道类型化门禁，
 * 真正的行级权限仍由 lib/access.ts 的 resolveStudentAccess 负责。
 */
export function canAccessFamilyData(role: string | null | undefined): boolean {
  return role === 'parent' || role === 'student';
}

/**
 * 谁能自助管理 TOTP（绑定 / 校验 / 关闭）：家长与系统管理员。
 * 学生没有二次验证的概念 —— 这里不写 `role !== 'student'`，
 * 是因为将来新增角色时，"默认放行"比"默认拒绝"危险得多。
 */
export function canManageTotp(role: string | null | undefined): boolean {
  return role === 'parent' || role === SYSTEM_ADMIN_ROLE;
}

/** 管理员是否已绑定 TOTP（绑定前一切管理能力都不生效，见方案 2B.2 第一道防线）。 */
export function hasAdminTotp(actor: {
  role: string | null | undefined;
  totpEnabledAt: Date | string | null | undefined;
}): boolean {
  return isSystemAdmin(actor.role) && actor.totpEnabledAt != null;
}

/**
 * 管理后台的**完整门禁**：角色是管理员 **且** 已绑定 TOTP。
 * 后端用它返回 403 TOTP_REQUIRED，前端用它决定"只能看到 TOTP 绑定页"。
 */
export function canAccessAdminConsole(actor: {
  role: string | null | undefined;
  totpEnabledAt: Date | string | null | undefined;
}): boolean {
  return hasAdminTotp(actor);
}

/**
 * 管理员能否查看/导出家庭数据 —— **恒为 false**（方案 2B.1 的硬边界）。
 *
 * 写成函数而不是"记得别去查"，是为了让这条边界在代码里可被检索、可被反复引用：
 * 任何 admin 路由想要返回家庭字段时，都必须先绕过这个恒 false 的判断，
 * code review 一眼就能看到。
 */
export function canAdminViewFamilyData(): false {
  return false;
}

/**
 * 允许由管理后台发起的写操作（见方案 2B.3）。
 *
 * 刻意**不包含** D 类能力：改 .env/密钥、重启或停止容器、删除数据卷、
 * 直连数据库执行 SQL、SSH 执行任意命令、导出孩子数据明细。
 * 这些只能在服务器上手工执行 —— 管理面板不能杀死自己。
 *
 * ⚠️ 2026-10-20 起**移除了 `admin.user.reset_password`**：管理后台不再能重置
 *    任何人的密码（家长自己用邮箱重置、孩子由家长重置、运维走 CLI）。
 *    「重置密码」这个动作仍然存在，但它的载体变成了服务器上的命令行，
 *    所以审计动作名是 `admin.user.reset_password_cli`（actor=system）。
 *    留在白名单里的每一项，都对应一个**网页上真的存在**的能力。
 */
export const ADMIN_ACTIONS = [
  'admin.user.revoke_sessions',
  'admin.user.disable',
  'admin.user.enable',
  'admin.settings.update',
  'admin.invite_code.create',
  'admin.mail_test.send',
] as const;

export type AdminAction = (typeof ADMIN_ACTIONS)[number];

export function isAdminAction(action: string): action is AdminAction {
  return (ADMIN_ACTIONS as readonly string[]).includes(action);
}

/**
 * 管理接口返回体里**绝不允许出现**的家庭内容字段（自查清单，见方案 2B.1）。
 * 后端在返回管理响应前会跑一遍 `findFamilyDataFields`，
 * 一旦命中就把字段路径打进日志 —— 这是"防回归"的自动提醒，不是安全边界。
 */
export const ADMIN_FORBIDDEN_FAMILY_FIELDS = [
  'studentNickname',
  'studentName',
  'checkinNote',
  'habitName',
  'nodeName',
  'commentContent',
  'rewardName',
  'redemptionNote',
  'birthday',
  'gradeCode',
  'uiMode',
  'mood',
  'pointsBalance',
  'ledger',
  'delta',
] as const;

/**
 * 递归扫描一个响应体，返回命中的家庭内容字段路径（空数组表示干净）。
 * 仅用于管理接口的自查与单测，不要放进用户请求的主路径里做重活。
 */
export function findFamilyDataFields(payload: unknown, maxDepth = 4): string[] {
  const hits: string[] = [];
  const forbidden = new Set<string>(ADMIN_FORBIDDEN_FAMILY_FIELDS);

  const walk = (value: unknown, path: string, depth: number): void => {
    if (depth > maxDepth || value === null || typeof value !== 'object') return;
    if (Array.isArray(value)) {
      value.slice(0, 50).forEach((item, index) => walk(item, `${path}[${index}]`, depth + 1));
      return;
    }
    for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
      const childPath = path ? `${path}.${key}` : key;
      if (forbidden.has(key)) hits.push(childPath);
      walk(child, childPath, depth + 1);
    }
  };

  walk(payload, '', 0);
  return hits;
}

/**
 * 审计日志的 actor_type 只能取 parent/student/system（与 DB 枚举一致）。
 * 系统管理员的登录与操作一律记成 `system`，避免为了一个新角色去改枚举、
 * 也避免审计表里出现"看起来像家庭用户"的管理员记录。
 */
export function toAuditActorType(role: string | null | undefined): 'parent' | 'student' | 'system' {
  if (role === 'parent') return 'parent';
  if (role === 'student') return 'student';
  return 'system';
}

/** 各角色登录后的默认落点（前端路由守卫用它避免"管理员被踢去家长首页"的死循环）。 */
export function homePathForRole(role: string | null | undefined): string {
  if (role === 'student') return '/student';
  if (isSystemAdmin(role)) return '/admin';
  return '/';
}
